import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { TemplateBehavior, Behavior } from "@/types/behavior.types";

interface ExploreTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId?: string;
  organizationId: string;
  onBehaviorAdded: (behavior: Behavior) => void;
}

const ExploreTemplatesModal = ({ 
  isOpen, 
  onClose, 
  agentId, 
  organizationId,
  onBehaviorAdded 
}: ExploreTemplatesModalProps) => {
  const [templateBehaviors, setTemplateBehaviors] = useState<TemplateBehavior[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addingBehaviorId, setAddingBehaviorId] = useState<string | null>(null);
  const { toast } = useToast();

  const isOrganizationLevel = !agentId;

  useEffect(() => {
    if (isOpen) {
      fetchTemplateBehaviors();
    }
  }, [isOpen]);

  const fetchTemplateBehaviors = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("template_behaviors")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      setTemplateBehaviors(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error fetching template behaviors",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBehavior = async (templateBehavior: TemplateBehavior) => {
    setAddingBehaviorId(templateBehavior.id);
    try {
      const insertData: any = {
        title: templateBehavior.title,
        description: templateBehavior.description,
        comment: templateBehavior.comment,
        organization_id: organizationId,
      };

      // Only add agent_id if we're creating an agent-specific behavior
      if (!isOrganizationLevel) {
        insertData.agent_id = agentId;
      }

      const { data: newBehavior, error } = await supabase
        .from("behaviors")
        .insert(insertData)
        .select("*")
        .single();

      if (error) throw error;
      
      toast({
        title: "Behavior added",
        description: `"${templateBehavior.title}" has been added${isOrganizationLevel ? " to your organization" : " to your agent"}.`,
      });
      
      onBehaviorAdded(newBehavior as Behavior);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error adding behavior",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setAddingBehaviorId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="h-screen w-[60%] max-w-none m-0 p-0 translate-x-0 translate-y-0 rounded-none border-l border-y-0 border-r-0 border-t-0 fixed right-0 top-0 inset-y-0 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right [&>button]:hidden"
        style={{ right: 0, left: 'auto', transform: 'none' }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col h-screen">
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-4 border-b bg-white flex-shrink-0 z-10">
            <div>
              <h2 className="text-lg font-semibold">Explore Templates</h2>
              <p className="text-sm text-muted-foreground">
                Add pre-built behaviors to your {isOrganizationLevel ? "organization" : "agent"}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="p-4 pb-8">
              {isLoading ? (
                <div className="grid grid-cols-2 gap-3">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="h-32"></CardContent>
                    </Card>
                  ))}
                </div>
              ) : templateBehaviors.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No template behaviors available</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {templateBehaviors.map(templateBehavior => (
                    <Card key={templateBehavior.id} className="hover:shadow-md transition-shadow flex flex-col h-full">
                      <CardHeader className="p-3 pb-2 flex-shrink-0">
                        <CardTitle className="!text-sm !font-medium flex items-center gap-2 !m-0" style={{ fontSize: '14px', margin: '0' }}>
                          {templateBehavior.emoji && (
                            <span className="text-base">{templateBehavior.emoji}</span>
                          )}
                          {templateBehavior.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 pt-0 flex-1 flex flex-col">
                        {templateBehavior.comment && (
                          <p className="text-sm text-muted-foreground mb-3 flex-1" style={{ fontSize: '14px' }}>
                            {templateBehavior.comment}
                          </p>
                        )}
                        <div className="mt-auto">
                          <Button 
                            onClick={() => handleAddBehavior(templateBehavior)}
                            disabled={addingBehaviorId === templateBehavior.id}
                            size="sm"
                            className="w-auto"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            {addingBehaviorId === templateBehavior.id ? "Adding..." : "Add"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExploreTemplatesModal; 